import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { auth } from '../../firebase';
import { ApiConstants } from '../../utils/apiConstants';
import { showToast } from '../../utils/toast';
import backgroundImage from '../../assets/background.png';
import logoText from '../../assets/logo.png';
import logo from '../../assets/logo.png';
import './index.css';

const OTPVerification = () => {
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [isResendEnabled, setIsResendEnabled] = useState(false);
    const [timer, setTimer] = useState(30);
    const [phone, setPhone] = useState('');
    const navigate = useNavigate();
    const inputRefs = useRef([]);

    // reCAPTCHA cleanup and setup for Resend
    const cleanupRecaptcha = () => {
        try { if (window.recaptchaVerifier) { window.recaptchaVerifier.clear(); window.recaptchaVerifier = null; } } catch (e) { }
        const c = document.getElementById('recaptcha-container');
        if (c) {
            const parent = c.parentNode;
            if (parent) {
                const newC = document.createElement('div');
                newC.id = 'recaptcha-container';
                parent.replaceChild(newC, c);
            } else {
                c.innerHTML = '';
            }
        }
    };

    const setupRecaptcha = () => new Promise((resolve, reject) => {
        cleanupRecaptcha();
        setTimeout(() => { 
            try { 
                const v = new RecaptchaVerifier(auth, 'recaptcha-container', { 
                    size: 'invisible', 
                    callback: () => { }, 
                    'expired-callback': () => cleanupRecaptcha(), 
                    'error-callback': () => cleanupRecaptcha() 
                }); 
                window.recaptchaVerifier = v; 
                resolve(v); 
            } catch (err) { 
                reject(err); 
            } 
        }, 300);
    });

    useEffect(() => {
        // Get phone number from localStorage
        const storedPhone = localStorage.getItem('phone');
        if (!storedPhone) {
            showToast('Phone number not found. Please login again.', 'error');
            navigate('/login');
            return;
        }
        setPhone(storedPhone);

        return () => cleanupRecaptcha();
    }, [navigate]);

    // Timer countdown
    useEffect(() => {
        let interval = null;
        if (timer > 0 && !isResendEnabled) {
            interval = setInterval(() => {
                setTimer((prev) => {
                    if (prev <= 1) {
                        setIsResendEnabled(true);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [timer, isResendEnabled]);

    // Handle OTP input change
    const handleOtpChange = (index, value) => {
        if (isNaN(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        // Move to next input
        if (value !== '' && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    // Handle backspace and Enter
    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        } else if (e.key === 'Enter') {
            if (otp.join('').length === 6 && !loading) {
                verifyOtpAndSignIn();
            }
        }
    };

    // Login API Call
    const callLoginAPI = async () => {
        const url = `${ApiConstants.baseUrl}${ApiConstants.login}`;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phoneNumber: phone }),
            });

            if (response.status === 200) {
                const responseData = await response.json();
                console.log('Login Response:', responseData);

                // Store tokens and user data in localStorage
                // API returns 'access_token' and 'refresh_token' (with underscores)
                const accessToken = responseData.access_token || responseData.accessToken || '';
                const refreshToken = responseData.refresh_token || responseData.refreshToken || '';

                localStorage.setItem('access_token', accessToken);
                localStorage.setItem('refresh_token', refreshToken);
                localStorage.setItem('token', accessToken); // Primary token key for API calls

                console.log('✅ Token saved to localStorage');

                if (responseData.userData) {
                    localStorage.setItem('pstatus', responseData.userData.pstatus || '');
                    localStorage.setItem('userid', responseData.userData.userId || '');
                    localStorage.setItem('name_key', responseData.userData.userName || '');
                    localStorage.setItem('userAccount', responseData.userData.userCategory || '');
                    localStorage.setItem('email_key', responseData.userData.userEmailid || '');
                    localStorage.setItem('contact_key', responseData.userData.userContact || '');
                    localStorage.setItem('userProfileImage', responseData.userData.userProfileImage || '');
                    localStorage.setItem('userlookingFor', responseData.userData.userLookingFor || '');
                    localStorage.setItem('skill_Update', responseData.skill_Update || false);
                }

                showToast(responseData.message || 'Login successful', 'success');

                // Navigate to home
                setTimeout(() => {
                    navigate('/', { replace: true });
                }, 500);
            } else {
                const errorData = await response.json();
                showToast(errorData.message || 'Login failed', 'error');
            }
        } catch (e) {
            console.error('Login API Error:', e);
            showToast('An error occurred during login', 'error');
        }
    };

    // Verify OTP and Sign In
    const verifyOtpAndSignIn = async () => {
        const otpCode = otp.join('');

        if (otpCode.length !== 6) {
            showToast('Please enter the 6-digit OTP code', 'error');
            return;
        }

        setLoading(true);

        try {
            // Get the confirmation result from window (stored during login)
            const confirmationResult = window.confirmationResult;

            if (!confirmationResult) {
                throw new Error('Verification session expired. Please login again.');
            }

            // Verify the OTP with Firebase
            const userCredential = await confirmationResult.confirm(otpCode);
            const user = userCredential.user;

            if (user) {
                console.log('Phone Verified Successfully:', user);
                showToast('Phone verified successfully!', 'success');

                // Call the login API
                await callLoginAPI();
            } else {
                throw new Error('User not found after OTP verification');
            }
        } catch (error) {
            console.error('Verification Error:', error);
            setLoading(false);

            if (error.code === 'auth/code-expired' || error.code === 'auth/session-expired') {
                showToast('OTP has expired. Please click Resend Code.', 'error');
            } else if (error.code === 'auth/invalid-verification-code') {
                showToast('Invalid OTP. Please check the code and try again.', 'error');
            } else {
                showToast(`Verification failed: ${error.message}`, 'error');
            }
        }
    };

    // Resend OTP
    const resendCode = async () => {
        if (loading) return;
        setLoading(true);
        try {
            const v = await setupRecaptcha();
            await v.render();
            const confirmationResult = await signInWithPhoneNumber(auth, phone, v);
            window.confirmationResult = confirmationResult;

            setTimer(30);
            setIsResendEnabled(false);
            setOtp(['', '', '', '', '', '']);
            showToast('A new OTP has been sent successfully.', 'success');
        } catch (error) {
            cleanupRecaptcha();
            console.error('Resend Error:', error);
            showToast(error.code === 'auth/too-many-requests' ? 'Too many attempts. Try again later.' : error.message || 'Failed to resend code', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="clapkart-login-auth-container">
            <div id="recaptcha-container"></div>
            <div className="clapkart-login-auth-left" style={{ backgroundImage: `url(${backgroundImage})` }}>
                <img src={logoText} alt="CLAP KARTEL" className="clapkart-login-clap-kartel-logo" />
            </div>

            <div className="clapkart-login-auth-right">
                <div className="clapkart-login-auth-form-container">
                    <div className="clapkart-login-logo-container">
                        <img src={logo} alt="Clap Kartel Logo" className="clapkart-login-form-logo" />
                    </div>

                    <h2 className="clapkart-login-auth-title">Verification Code</h2>
                    <p className="clapkart-login-auth-subtitle">
                        We have sent a verification code to:<br />
                        <strong>{phone}</strong>
                    </p>

                    {/* OTP Input Fields */}
                    <div className="otp-container">
                        {otp.map((digit, index) => (
                            <input
                                key={index}
                                ref={(el) => (inputRefs.current[index] = el)}
                                type="text"
                                maxLength="1"
                                value={digit}
                                onChange={(e) => handleOtpChange(index, e.target.value)}
                                onKeyDown={(e) => handleKeyDown(index, e)}
                                className="otp-input"
                                disabled={loading}
                            />
                        ))}
                    </div>

                    {/* Timer and Resend */}
                    <div style={{ textAlign: 'center', marginTop: '20px', marginBottom: '20px' }}>
                        <p style={{ fontSize: '14px', color: '#3498db', marginBottom: '10px' }}>
                            {timer > 0 ? `Resend Code in ${timer} seconds` : 'You can resend the code now'}
                        </p>
                        <button
                            onClick={resendCode}
                            disabled={!isResendEnabled}
                            className="resend-button"
                            style={{
                                background: 'none',
                                border: 'none',
                                color: isResendEnabled ? '#3498db' : '#ccc',
                                textDecoration: 'underline',
                                cursor: isResendEnabled ? 'pointer' : 'not-allowed',
                                fontSize: '14px',
                            }}
                        >
                            Resend Code
                        </button>
                    </div>

                    {/* Login Button */}
                    <button
                        onClick={verifyOtpAndSignIn}
                        className="clapkart-login-submit-button"
                        disabled={loading || otp.join('').length !== 6}
                    >
                        {loading ? 'Verifying...' : 'Login'}
                    </button>

                    {/* Loading Dialog */}
                    {loading && (
                        <div className="loading-overlay">
                            <div className="loading-dialog">
                                <div className="loader"></div>
                                <p style={{ marginTop: '10px', color: '#333' }}>Please wait...</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OTPVerification;
